import { useEffect, useState } from "react";

import {
  useFloating,
  autoUpdate,
  offset as floatingOffset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  type UseHoverProps,
  type UseFocusProps,
  type Placement,
} from "@floating-ui/react";

import { getIsTouchDevice } from "@/utils/getIsTouchDevice";
import { twMerge } from "@/utils/twMerge";

export type UseTooltipProps = {
  title: React.ReactNode;
  content?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  offset?: number;
  open?: boolean;
  delay?: UseHoverProps["delay"];
  hoverProps?: UseHoverProps;
  focusProps?: UseFocusProps;
  placement?: Placement;
};

export function useTooltip({
  title,
  content,
  className,
  disabled,
  open = false,
  offset = 5,
  hoverProps = {},
  focusProps = {},
  placement = "top",
}: UseTooltipProps) {
  const [isOpen, setIsOpen] = useState(open);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      floatingOffset(offset),
      flip({
        fallbackAxisSideDirection: "start",
      }),
      shift(),
    ],
  });

  const hover = useHover(context, {
    move: true,
    ...hoverProps,
  });
  const focus = useFocus(context, focusProps);
  const dismiss = useDismiss(context, {
    referencePress: true,
  });
  const role = useRole(context, {
    role: "tooltip",
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  useEffect(() => {
    if (disabled && isOpen) {
      setIsOpen(false);
    }
  }, [isOpen, disabled]);

  const hasRenderableContent = title || content;

  if (getIsTouchDevice() || !hasRenderableContent) {
    return { handlerProps: {}, tooltip: null };
  }

  return {
    handlerProps: {
      ref: refs.setReference,
      ...getReferenceProps(),
    },
    tooltip: isOpen && (
      <FloatingPortal id="tooltip-root">
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className={twMerge(className, "z-50 tracking-tight")}
          {...getFloatingProps()}
        >
          {title}
          {content && (
            <span className="text-size-14 text-gray-300">
              <br />
              {content}
            </span>
          )}
        </div>
      </FloatingPortal>
    ),
  };
}

export function useTooltipDefault({
  title,
  content,
  className,
  hoverProps,
  ...props
}: UseTooltipProps) {
  return useTooltip({
    title,
    content,
    hoverProps: {
      delay: {
        open: 100,
        close: 0,
      },
      enabled: !!title,
      ...hoverProps,
    },
    className: twMerge(
      "w-[max-content] max-w-[250px] bg-black/70 dark:bg-gray-700/80 backdrop-blur-xs text-white text-xs rounded-lg px-2.5 py-2.5 z-50",
      className,
    ),
    ...props,
  });
}
